import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import flightRosterLogo from "../../../assets/images/flight-roster-icon.svg";
import CommercialAirlineNavigationBar from "../../../components/element/CommercialAirlineNavigationBar";
import SnackbarAlert from "../../../components/element/SnackbarAlert";
import { useFormData } from "../../../helpers/FormDataProvider";
import useAirlineAdminApi from "../../../services/api/useAirlineAdminApi";
import { StorageContext } from "../../../services/StorageProvider";
import { BADGE_TYPES, PAGINATION_PAGE_SIZE } from "../../../utils/constants";
import ROUTE_PATHS from "../../../utils/routePaths";
import {
  normalizeCommercialFlightPlan,
  setHistoricalFlightPlanData,
} from "../../../utils/utils";
import "../Disputes/css/FlightDisputeCard.css";
import "../Disputes/css/PassengerCountDisputeFilter.css";
import "../Disputes/PassengerDisputeCount.css";
import PassengerCountDisputeFilter from "../Disputes/Views/PassengerCountDisputeFilter";
import "./FlightRoster.css";
import FlightRosterFilter from "./Views/FlightRosterFilter";
import FlightRosterGridComponent from "./Views/FlightRosterGridComponent";
import FlightRosterSearch from "./Views/FlightRosterSearch";
import PassengerInfoComponent from "./Views/PassengerInfoComponent";

// Helper function to transform response data from new API structure
// The new API wraps flight plan data in a 'cfp' object with an 'escortServiceDto' sibling
const transformFlightPlanResponse = (response) => {
  if (!response || !response.data || !response.data.content) {
    return response;
  }
  return {
    ...response,
    data: {
      ...response.data,
      content: response.data.content.map((item) =>
        normalizeCommercialFlightPlan(item)
      ),
    },
  };
};


const FlightRoster = () => {
  const navigate = useNavigate();
  const { userData } = useFormData();
  const { airlineData } = useContext(StorageContext);
  const queryClient = useQueryClient();

  // Tab state management
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem("flightRosterActiveTab") || "current";
  });
  const [currentPage, setCurrentPage] = useState(() => {
    return sessionStorage.getItem("flightRosterCurrentPage")
      ? Number(sessionStorage.getItem("flightRosterCurrentPage"))
      : 0;
  });
  const [isSearching, setIsSearching] = useState(false);
  const pageSize = PAGINATION_PAGE_SIZE;
  const {
    fetchCommercialFlightPlans,
    searchFlightPlan,
    filterCommercialFlightPlans,
    fetchHistoricCommercialFlightPlans,
    filterHistoricCommercialFlightPlans,
    fetchCommercialFutureFlightPlans,
    filterCommercialFutureFlightPlans,
    fetchCommercialCurrentFlightPlansSummary,
    filterCommercialCurrentFlightPlansSummary,
    getAirlineById,
    fetchPassengerDispute,
  } = useAirlineAdminApi(userData.isAAUser);
  const [flightPlanData, setFlightPlanData] = useState(null);
  const [filterApplied, setFilterApplied] = useState(false);
  const [selectedAirline, setSelectedAirline] = useState(null);
  const [selectedArrivalAirport, setSelectedArrivalAirport] = useState(null);
  const [selectedDepartureAirport, setSelectedDepartureAirport] =
    useState(null);
  const [selectedDepartureDate, setSelectedDepartureDate] = useState(null);
  const [selectedBadges, setSelectedBadges] = useState([]);
  const [searchingText, setSearchingText] = useState("");
  const [airlineNames, setAirlineNames] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(true);

  // Dispute-related state
  const [disputeFilterFlightNumber, setDisputeFilterFlightNumber] =
    useState("");
  const [disputeFilterSelectedDate, setDisputeFilterSelectedDate] =
    useState(null);
  const [disputeSelectedBadges, setDisputeSelectedBadges] = useState([]);

  // Tab configuration
  const tabs = [
    { key: "historical", label: "Historical Flight Rosters" },
    { key: "current", label: "Current Flight Rosters" },
    { key: "future", label: "Future Flight Rosters" },
    { key: "dispute", label: "Disputed Flight Rosters" },
  ];

  const searchHistoricalFlightPlan = async (planData) => {
    return await searchFlightPlan(planData);
  };

  const getTabAPIs = () => {
    switch (activeTab) {
      case "historical":
        return {
          fetchFlights: fetchHistoricCommercialFlightPlans,
          filterFlights: filterHistoricCommercialFlightPlans,
          searchFlights: searchHistoricalFlightPlan,
        };
      case "future":
        return {
          fetchFlights: fetchCommercialFutureFlightPlans,
          filterFlights: filterCommercialFutureFlightPlans,
          searchFlights: searchFlightPlan,
        };
      case "dispute":
        return {
          fetchFlights: fetchPassengerDispute,
          filterFlights: fetchPassengerDispute,
          searchFlights: fetchPassengerDispute,
        };
      default:
        return {
          fetchFlights: fetchCommercialFlightPlans,
          filterFlights: filterCommercialFlightPlans,
          searchFlights: searchFlightPlan,
          ...(userData.isAAUser === true && {
            fetchSummary: fetchCommercialCurrentFlightPlansSummary,
            filterSummary: filterCommercialCurrentFlightPlansSummary,
          }),
        };
    }
  };

  const {
    fetchFlights,
    filterFlights,
    searchFlights,
    fetchSummary,
    filterSummary,
  } = getTabAPIs();

  const {
    data: flightPlanApiResponse,
    isLoading: isFlightPlanLoading,
    error: flightPlanError,
  } = useQuery({
    queryKey: [
      "get-flight-plans",
      activeTab,
      currentPage,
      disputeFilterFlightNumber,
      disputeFilterSelectedDate,
      disputeSelectedBadges.join(","),
    ],
    queryFn: async () => {
      if (activeTab === "dispute") {
        return await fetchFlights({
          page: currentPage,
          size: pageSize,
          ...(disputeFilterFlightNumber && {
            flightNumber: disputeFilterFlightNumber,
          }),
          ...(disputeFilterSelectedDate && {
            flightDate: dayjs(disputeFilterSelectedDate).format("YYYY-MM-DD"),
          }),
          ...(userData.isAAUser &&
            disputeSelectedBadges?.includes(BADGE_TYPES.ADSB_VERIFIED) && {
              [BADGE_TYPES.ADSB_VERIFIED]: true,
            }),
          ...(userData.isAAUser &&
            disputeSelectedBadges?.includes(BADGE_TYPES.BMS_VERIFIED) && {
              [BADGE_TYPES.BMS_VERIFIED]: true,
            }),
        });
      }
      return await fetchFlights({
        page: currentPage,
        size: pageSize,
      });
    },
    enabled:
      !filterApplied &&
      !isSearching &&
      userData.isAAUser !== null &&
      userData.isAAUser !== undefined,
    retry: false,
  });

  // Separate query for summary data - only for current tab and when refreshing
  // This query should ONLY run when userData.isAAUser === true
  const {
    data: summaryData,
    isLoading: isSummaryLoading,
    error: summaryError,
  } = useQuery({
    queryKey: ["get-flight-plans-summary", activeTab, userData.isAAUser],
    queryFn: async () => {
      const summaryResponse = await fetchSummary();
      setIsRefreshing(false);
      return summaryResponse.data;
    },
    enabled:
      activeTab === "current" &&
      !filterApplied &&
      !isSearching &&
      userData.isAAUser === true &&
      typeof fetchSummary === "function",
    retry: false,
  });

  // Combine flight plan data with summary data for current tab
  const combinedFlightPlanData = React.useMemo(() => {
    const transformedResponse = transformFlightPlanResponse(
      flightPlanApiResponse
    );
    if (activeTab === "current" && summaryData && transformedResponse) {
      return {
        ...transformedResponse,
        data: {
          ...transformedResponse?.data,
          summary: summaryData,
        },
      };
    }
    return transformedResponse;
  }, [flightPlanApiResponse, summaryData, activeTab]);

  const searchFlightPlanMutation = useMutation({
    mutationFn: () =>
      searchFlights({
        page: currentPage,
        size: pageSize,
        flightNumber: searchingText,
        cfpSearchType:
          activeTab === "historical"
            ? "HISTORIC"
            : activeTab === "future"
              ? "FUTURE"
              : "CURRENT",
      }),
    onSuccess: (response) => {
      setFlightPlanData(transformFlightPlanResponse(response));
    },
    onError: () => {
      setFlightPlanData(null);
    },
    retry: false,
  });

  const filterFlightPlanDataMutation = useMutation({
    mutationFn: async () => {
      const filterParams = {
        page: currentPage,
        size: pageSize,
        ...(selectedAirline ? { airlineId: selectedAirline.id } : {}),
        ...(selectedArrivalAirport
          ? { arrivalAirportId: selectedArrivalAirport.id }
          : {}),
        ...(selectedDepartureAirport
          ? { departureAirportId: selectedDepartureAirport.id }
          : {}),
        ...(selectedDepartureDate && activeTab !== "current"
          ? {
            departureDate: dayjs(selectedDepartureDate).format("YYYY-MM-DD"),
          }
          : {}),
        ...(userData.isAAUser &&
          selectedBadges.includes(BADGE_TYPES.ADSB_VERIFIED) && {
            [BADGE_TYPES.ADSB_VERIFIED]: true,
          }),
        ...(userData.isAAUser &&
          selectedBadges.includes(BADGE_TYPES.BMS_VERIFIED) && {
            [BADGE_TYPES.BMS_VERIFIED]: true,
          }),
      };

      const flightPlansResponse = await filterFlights(filterParams);
      const transformedResponse =
        transformFlightPlanResponse(flightPlansResponse);

      // If it's current tab and user is AA user, also fetch the filtered summary
      // Only call if filterSummary is actually a function (only available when isAAUser === true)
      if (activeTab === "current" && userData.isAAUser === true && typeof filterSummary === "function") {
        // Check if any other filters (airline, arrival airport, departure airport) are selected along with badges
        const hasOtherFilters = selectedAirline || selectedArrivalAirport || selectedDepartureAirport;
        
        // If badges are selected along with other filters, call snapshot API excluding badge parameters
        if (selectedBadges.length > 0 && hasOtherFilters) {
          const { [BADGE_TYPES.ADSB_VERIFIED]: _, [BADGE_TYPES.BMS_VERIFIED]: __, ...snapshotParams } = filterParams;
          const summaryResponse = await filterSummary(snapshotParams);
          return {
            ...transformedResponse,
            data: {
              ...transformedResponse.data,
              summary: summaryResponse.data,
            },
          };
        }
        // If no badges are selected, call snapshot API with all filters
        else if (selectedBadges.length === 0) {
          const summaryResponse = await filterSummary(filterParams);
          return {
            ...transformedResponse,
            data: {
              ...transformedResponse.data,
              summary: summaryResponse.data,
            },
          };
        }
        // If only badges are selected (no other filters), don't call snapshot API
        // Summary will be preserved in onSuccess callback
      }

      return transformedResponse;
    },
    onSuccess: (response) => {
      // If only badges are selected (no other filters), preserve existing summary data
      const hasOtherFilters = selectedAirline || selectedArrivalAirport || selectedDepartureAirport;
      if (activeTab === "current" && userData.isAAUser === true && selectedBadges.length > 0 && !hasOtherFilters) {
        const existingSummary = flightPlanData?.data?.summary || summaryData;
        if (existingSummary) {
          setFlightPlanData({
            ...response,
            data: {
              ...response.data,
              summary: existingSummary,
            },
          });
        } else {
          setFlightPlanData(response);
        }
      } else {
        setFlightPlanData(response);
      }
    },
    onError: () => {
      setFlightPlanData(null);
    },
    retry: false,
  });

  const fetchAirlineMutation = useMutation({
    mutationFn: async (airlineId) => {
      const response = await getAirlineById(airlineId);
      return { id: airlineId, name: response?.data?.airlineName || "" };
    },
    onSuccess: (data) => {
      setAirlineNames((prev) => {
        const newNames = { ...prev, [data.id]: data.name };
        localStorage.setItem("airlineNames", JSON.stringify(newNames));
        return newNames;
      });
    },
    onError: (error, airlineId) => {
      console.error(`Error fetching airline name for ID ${airlineId}:`, error);
      setAirlineNames((prev) => {
        const newNames = { ...prev, [airlineId]: "" };
        localStorage.setItem("airlineNames", JSON.stringify(newNames));
        return newNames;
      });
    },
    retry: false,
  });

  useEffect(() => {
    if (userData.isAAUser === null || userData.isAAUser === undefined) return;

    // Use tab-specific storage keys for search and filter data
    const searchStoredData = localStorage.getItem(
      `flightRosterSearchData_${activeTab}`
    );
    const filterStoredData = localStorage.getItem(
      `flightRosterFilterData_${activeTab}`
    );

    if (searchStoredData) {
      const searchParsedData = JSON.parse(searchStoredData);
      setSearchingText(searchParsedData);
      setIsSearching(true);
      const timer = setTimeout(() => {
        searchFlightPlanMutation.mutate();
      }, 100);
      return () => clearTimeout(timer);
    } else if (filterStoredData) {
      const filterParsedData = JSON.parse(filterStoredData);
      setSelectedAirline(filterParsedData?.airline);
      setSelectedArrivalAirport(filterParsedData?.arrivalAirport);
      setSelectedDepartureAirport(filterParsedData?.departureAirport);
      setSelectedDepartureDate(
        filterParsedData?.departureDate
          ? dayjs(filterParsedData?.departureDate)
          : null
      );
      setSelectedBadges(filterParsedData?.badges || []);
      setFilterApplied(true);
      const timer = setTimeout(() => {
        filterFlightPlanDataMutation.mutate();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setFlightPlanData(combinedFlightPlanData || null);
    }
  }, [combinedFlightPlanData, activeTab, userData.isAAUser]);

  useEffect(() => {
    sessionStorage.setItem("flightRosterCurrentPage", currentPage);
  }, [currentPage]);

  useEffect(() => {
    sessionStorage.setItem("flightRosterActiveTab", activeTab);
  }, [activeTab]);

  // Effect to initialize airline names from airlineData
  useEffect(() => {
    if (
      !airlineData?.data ||
      userData.isAAUser === null ||
      userData.isAAUser === undefined
    )
      return;

    const initialAirlineNames = {};
    airlineData.data.forEach((airline) => {
      if (airline?.id && airline?.airlineName) {
        initialAirlineNames[airline.id] = airline.airlineName;
      }
    });
    setAirlineNames((prev) => {
      const newNames = { ...prev, ...initialAirlineNames };
      localStorage.setItem("airlineNames", JSON.stringify(newNames));
      return newNames;
    });
  }, [airlineData, userData.isAAUser]);

  // Effect to fetch airline names when flightPlanData changes
  useEffect(() => {
    if (
      !flightPlanData?.data?.content ||
      userData.isAAUser === null ||
      userData.isAAUser === undefined ||
      fetchAirlineMutation.isPending
    )
      return;

    const storedAirlineNames = JSON.parse(
      localStorage.getItem("airlineNames") || "{}"
    );
    const processedAirlines = new Set();

    // Add additional safety check for content array
    if (Array.isArray(flightPlanData.data.content)) {
      flightPlanData.data.content.forEach((item) => {
        if (
          item.airlineId &&
          !processedAirlines.has(item.airlineId) &&
          !airlineNames[item.airlineId] &&
          !storedAirlineNames[item.airlineId] &&
          !fetchAirlineMutation.variables?.includes(item.airlineId)
        ) {
          processedAirlines.add(item.airlineId);
          fetchAirlineMutation.mutate(item.airlineId);
        }
      });
    }
  }, [flightPlanData?.data?.content, userData.isAAUser]);

  // Reset refresh flag when tab changes
  useEffect(() => {
    setIsRefreshing(true);
  }, [activeTab]);

  // Effect to automatically clear search when search text becomes empty
  useEffect(() => {
    if (isSearching && searchingText === "") {
      setCurrentPage(0);
      setFlightPlanData(combinedFlightPlanData || null);
      setIsSearching(false);
      localStorage.removeItem(`flightRosterSearchData_${activeTab}`);
    }
  }, [searchingText, isSearching, combinedFlightPlanData, activeTab]);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setCurrentPage(0);
    setIsSearching(false);
    setFilterApplied(false);
    setSearchingText("");
    // Clear dispute filters when switching tabs
    setDisputeFilterFlightNumber("");
    setDisputeFilterSelectedDate(null);
    setDisputeSelectedBadges([]);
    // Clear stored search and filter data for all tabs when switching
    localStorage.removeItem("flightRosterSearchData_historical");
    localStorage.removeItem("flightRosterSearchData_current");
    localStorage.removeItem("flightRosterSearchData_future");
    localStorage.removeItem("flightRosterFilterData_historical");
    localStorage.removeItem("flightRosterFilterData_current");
    localStorage.removeItem("flightRosterFilterData_future");
    // Reset filter selections
    setSelectedAirline(null);
    setSelectedArrivalAirport(null);
    setSelectedDepartureAirport(null);
    setSelectedDepartureDate(null);
    setSelectedBadges([]);
  };

  const handleFlightClick = (item) => {
    if (activeTab === "historical") {
      setHistoricalFlightPlanData(item);
      navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_HISTORICAL_DATA_VIEW);
    } else if (activeTab === "dispute") {
      // Save flight data to sessionStorage for persistence across page refreshes
      sessionStorage.setItem("disputeDetailsFlightData", JSON.stringify(item));
      navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_PASSENGER_DISPUTE_COUNT_DETAILS, {
        state: { flightData: item },
      });
    } else {
      localStorage.setItem("flightPlanData", JSON.stringify(item));
      navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_FLIGHT_ROSTER_EDIT);
    }
  };

  const handleCreateFlightPlanClick = () => {
    navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_CREATE_FLIGHT_PLAN);
  };

  const handleNextPage = () => {
    if (currentPage + 1 < flightPlanData?.data?.totalPages) {
      setCurrentPage((prev) => prev + 1);
      // Use the appropriate API based on the current state
      if (filterApplied) {
        filterFlightPlanDataMutation.mutate();
      } else if (isSearching) {
        searchFlightPlanMutation.mutate();
      }
      // Note: For normal pagination, useQuery will automatically refetch when currentPage changes
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
      if (filterApplied) {
        filterFlightPlanDataMutation.mutate();
      } else if (isSearching) {
        searchFlightPlanMutation.mutate();
      }
    }
  };

  const handleSearchClick = () => {
    setCurrentPage(0);
    setIsSearching(true);
    searchFlightPlanMutation.mutate();
    localStorage.setItem(
      `flightRosterSearchData_${activeTab}`,
      JSON.stringify(searchingText)
    );
  };

  const handleClearSearchClick = () => {
    setCurrentPage(0);
    setSearchingText("");
    setFlightPlanData(combinedFlightPlanData || null);
    setIsSearching(false);
    localStorage.removeItem(`flightRosterSearchData_${activeTab}`);
  };

  const handleFilterApplyClick = () => {
    setFilterApplied(true);
    setCurrentPage(0);
    filterFlightPlanDataMutation.mutate();
    const filterData = {
      airline: selectedAirline,
      arrivalAirport: selectedArrivalAirport,
      departureAirport: selectedDepartureAirport,
      departureDate: selectedDepartureDate,
      badges: selectedBadges,
    };
    localStorage.setItem(
      `flightRosterFilterData_${activeTab}`,
      JSON.stringify(filterData)
    );
  };

  const handleFilterClearClick = () => {
    setCurrentPage(0);
    setFlightPlanData(combinedFlightPlanData || null);
    setFilterApplied(false);
    setSelectedBadges([]);
    localStorage.removeItem(`flightRosterFilterData_${activeTab}`);
  };

  // Dispute filter handlers
  const handleDisputeFilterApply = (flightNumber, selectedDate, badges) => {
    setDisputeFilterFlightNumber(flightNumber);
    setDisputeFilterSelectedDate(selectedDate);
    setDisputeSelectedBadges(badges || []);
    setCurrentPage(0);
  };

  const handleDisputeFilterClear = () => {
    setDisputeFilterFlightNumber("");
    setDisputeFilterSelectedDate(null);
    setDisputeSelectedBadges([]);
    setCurrentPage(0);
  };

  return (
    <div>
      {searchFlightPlanMutation.isError &&
        searchFlightPlanMutation.error?.status !== 404 ? (
        <SnackbarAlert type={"error"}>
          {searchFlightPlanMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {filterFlightPlanDataMutation.isError &&
        filterFlightPlanDataMutation.error?.status !== 404 ? (
        <SnackbarAlert type={"error"}>
          {filterFlightPlanDataMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      <div className="flight-roster-container">
        <CommercialAirlineNavigationBar
          title={"Flight Roster"}
          navigationLogo={flightRosterLogo}
          showCreateFlightPlanButton={true}
          onClickCreateFlightPlan={handleCreateFlightPlanClick}
          backContent={
            <div className="navigation-header">
              <Link
                to={ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}
                className="navigation-header-link"
              >
                Home/
              </Link>
              Flight Roster
            </div>
          }
        />
        {/* Flight Roster Tabs */}
        <div className="flight-roster-tabs-container">
          <div className="flight-roster-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`flight-roster-tab ${activeTab === tab.key ? "active" : ""
                  }`}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === "dispute" ? (
          <>
            <PassengerCountDisputeFilter
              onClickApply={handleDisputeFilterApply}
              onClickClear={handleDisputeFilterClear}
              isAAUser={userData?.isAAUser}
              currentFlightNumber={disputeFilterFlightNumber}
              currentSelectedDate={disputeFilterSelectedDate}
              currentSelectedBadges={disputeSelectedBadges}
            />
            <div className="flight-roster-divider"></div>
          </>
        ) : (
          <>
            {!filterApplied && (
              <FlightRosterSearch
                searchFlightData={searchingText}
                setSearchFlightData={setSearchingText}
                onClickSearch={handleSearchClick}
                onClickClearSearch={handleClearSearchClick}
              />
            )}
            {!isSearching && (
              <>
                <div className="flight-roster-divider"></div>
                <FlightRosterFilter
                  selectedAirline={selectedAirline}
                  setSelectedAirline={setSelectedAirline}
                  selectedArrivalAirport={selectedArrivalAirport}
                  setSelectedArrivalAirport={setSelectedArrivalAirport}
                  selectedDepartureAirport={selectedDepartureAirport}
                  setSelectedDepartureAirport={setSelectedDepartureAirport}
                  selectedDepartureDate={selectedDepartureDate}
                  setSelectedDepartureDate={setSelectedDepartureDate}
                  selectedBadges={selectedBadges}
                  setSelectedBadges={setSelectedBadges}
                  onClickApply={handleFilterApplyClick}
                  onClickClear={handleFilterClearClick}
                  showDateFilter={activeTab !== "current"}
                />
              </>
            )}
          </>
        )}
        {userData.isAAUser &&
          flightPlanData?.data?.numberOfElements > 0 &&
          !isSearching && (
            <PassengerInfoComponent
              isVisible={activeTab === "current"}
              passengerData={flightPlanData?.data?.summary}
              isLoading={
                isFlightPlanLoading ||
                filterFlightPlanDataMutation.isPending ||
                (activeTab === "current" && isSummaryLoading)
              }
            />
          )}
        <div className="flight-roster-divider"></div>
        <div className="flight-roster-grid-header-container">
          <div className="flight-roster-grid-header">All Flights</div>
          <div className="flight-roster-pagination-header">{`Showing ${(flightPlanData?.data?.pageable?.offset ?? 0) +
            (flightPlanData?.data?.numberOfElements ?? 0)
            } of ${flightPlanData?.data?.totalElements ?? 0}`}</div>
        </div>
        {activeTab === "dispute" ? (
          <FlightRosterGridComponent
            isAAUser={userData.isAAUser}
            flightPlanData={flightPlanData}
            onClickCard={handleFlightClick}
            isLoading={isFlightPlanLoading}
            error={flightPlanError}
            airlineNames={airlineNames}
            isDisputeTab={true}
          />
        ) : (
          <FlightRosterGridComponent
            isAAUser={userData.isAAUser}
            flightPlanData={flightPlanData}
            onClickCard={handleFlightClick}
            isLoading={
              isSearching
                ? searchFlightPlanMutation?.isPending
                : filterApplied
                  ? filterFlightPlanDataMutation?.isPending
                  : isFlightPlanLoading
            }
            error={
              isSearching
                ? searchFlightPlanMutation?.error
                : filterApplied
                  ? filterFlightPlanDataMutation?.error
                  : flightPlanError || (activeTab === "current" && summaryError)
            }
            airlineNames={airlineNames}
            isDisputeTab={false}
          />
        )}
      </div>
      {flightPlanData?.data?.content &&
        flightPlanData?.data?.content.length > 0 && (
          <div className="pagination-container">
            <div className="pagination-content">
              Page {currentPage + 1} of {flightPlanData?.data?.totalPages}
            </div>
            <button
              className="pagination-button"
              onClick={handlePreviousPage}
              disabled={currentPage === 0}
            >
              <CaretLeftOutlined />
            </button>
            <button
              className="pagination-button"
              onClick={handleNextPage}
              disabled={currentPage + 1 === flightPlanData?.data?.totalPages}
            >
              <CaretRightOutlined />
            </button>
          </div>
        )}
    </div>
  );
};

export default FlightRoster;
